import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, ShieldCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import TestimonialBibliothek from '@/components/TestimonialBibliothek';
import { useAuth } from '@/contexts/AuthContext';
import { bibliothekCrm, fehlerText } from '@/lib/testimonialClient';

/* /testimonials (Coach-Cockpit W1.7): Bibliothek für Leitung und Content-Team. Oben für die Leitung der Schalter: erst
   genau diese Fassung des Einwilligungstexts freigeben (anwaltlich geprüft), dann einschalten — wie beim Essensfoto. */

function Schalter() {
  const [s, setS] = useState(null);
  const lade = useCallback(() => { bibliothekCrm.schalter().then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  useEffect(() => { lade(); }, [lade]);
  if (!s) return null;
  const setzen = async (daten, ok) => {
    try { const r = await bibliothekCrm.schalten(daten); setS(r.data); toast.success(ok); }
    catch (e) { toast.error(fehlerText(e, 'Nicht gespeichert')); }
  };
  const t = s.einwilligung;
  return (
    <Card className="border-border/60" data-testid="testimonial-schalter">
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><ShieldCheck className="h-4 w-4 text-marke" /> Testimonial-Einwilligung
          <Badge variant={s.aktiv ? 'default' : 'outline'} className="text-[10px]">{s.aktiv ? 'eingeschaltet' : 'aus'}</Badge>
          {t.entwurf && <Badge variant="outline" className="text-[10px]">Text: Entwurf</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="font-semibold">{t.kurzfrage}</p>
        <details>
          <summary className="cursor-pointer text-xs text-muted-foreground">Ganzen Text lesen (Fassung {t.version})</summary>
          <p className="mt-2 whitespace-pre-line rounded-lg bg-muted/50 p-3 text-xs leading-relaxed">{t.text}</p>
        </details>
        {s.text_freigegeben
          ? <p className="text-xs text-muted-foreground">Fassung freigegeben von {s.freigegeben?.von}.</p>
          : <p className="text-xs text-muted-foreground">Vor dem Freigeben vom Anwalt prüfen lassen. Wer den Text ändert, vergibt eine neue Fassung — dann ist die Freigabe hinfällig.</p>}
        <div className="flex flex-wrap gap-2">
          {!s.text_freigegeben && <Button variant="outline" className="min-h-11" onClick={() => setzen({ text_freigeben_version: t.version }, 'Fassung freigegeben')}>Diese Fassung freigeben (geprüft)</Button>}
          <Button className="min-h-11" disabled={!s.text_freigegeben} onClick={() => setzen({ aktiv: !s.aktiv }, s.aktiv ? 'Ausgeschaltet' : 'Eingeschaltet')}>{s.aktiv ? 'Ausschalten' : 'Einschalten'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TestimonialBibliothekPage() {
  const { user } = useAuth();
  const leitung = ['admin', 'manager'].includes(user?.role);
  useEffect(() => { document.title = 'Testimonials'; }, []);
  return (
    <div className="min-w-0 max-w-full space-y-4 p-3 sm:p-6" data-testid="testimonials-seite">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold sm:text-2xl"><Award className="h-6 w-6 text-marke" /> Testimonial-Bibliothek</h1>
        <p className="text-sm text-muted-foreground">Ergebnisse, die Mitglieder freigegeben haben — nur Zahlen und Fotos ohne Gesicht, nie ein Name. Widerruft jemand, verschwindet sein Material sofort.</p>
      </div>
      {leitung && <Schalter />}
      <TestimonialBibliothek client={bibliothekCrm} />
    </div>
  );
}
